import type { TemplateCategory } from '../hooks/useDocumentTemplates';

export interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  thumbnail: string;
  header_html: string;
  body_html: string;
  footer_html: string;
  css: string;
  sample_data: Record<string, unknown>;
}

interface StarterTemplatesProps {
  onSelect: (starter: StarterTemplate) => void;
  onSkip: () => void;
}

const INVOICE_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #4f46e5;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:18px;color:#1f2937;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#6b7280;">{{firm.address}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#6b7280;">{{firm.phone}} | {{firm.email}}</p>
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:28px;color:#4f46e5;letter-spacing:1px;">INVOICE</h1>
    <p style="margin:8px 0 2px;font-size:12px;"><strong>Invoice #:</strong> {{invoice.number}}</p>
    <p style="margin:2px 0;font-size:12px;"><strong>Date:</strong> {{formatDate invoice.issue_date}}</p>
    <p style="margin:2px 0;font-size:12px;"><strong>Due:</strong> {{formatDate invoice.due_date}}</p>
  </div>
</div>`;

const INVOICE_BODY = `<div style="margin-top:24px;">
  <div style="margin-bottom:20px;">
    <h3 style="font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin:0 0 6px;">Bill To</h3>
    <p style="margin:0;font-size:13px;font-weight:600;">{{client.business_name}}</p>
    <p style="margin:2px 0;font-size:12px;">{{client.name}}</p>
    <p style="margin:2px 0;font-size:12px;color:#6b7280;">{{client.address}}</p>
    {{#if client.email}}<p style="margin:2px 0;font-size:12px;color:#6b7280;">{{client.email}}</p>{{/if}}
  </div>

  <table style="width:100%;border-collapse:collapse;margin-top:16px;">
    <thead>
      <tr style="background:#f9fafb;">
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">#</th>
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Description</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Qty</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Rate</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Amount</th>
      </tr>
    </thead>
    <tbody>
      {{#each line_items}}
      <tr>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f3f4f6;">{{inc @index}}</td>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f3f4f6;">{{this.description}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f3f4f6;">{{this.quantity}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f3f4f6;">{{currency this.unit_price}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f3f4f6;">{{currency this.line_total}}</td>
      </tr>
      {{/each}}
    </tbody>
  </table>

  <div style="margin-top:16px;display:flex;justify-content:flex-end;">
    <div style="width:220px;">
      <div style="display:flex;justify-content:space-between;padding:6px 0;font-size:12px;">
        <span style="color:#6b7280;">Subtotal</span><span>{{currency invoice.subtotal}}</span>
      </div>
      <div style="display:flex;justify-content:space-between;padding:6px 0;font-size:12px;">
        <span style="color:#6b7280;">Tax</span><span>{{currency invoice.tax_amount}}</span>
      </div>
      <div style="display:flex;justify-content:space-between;padding:10px 0;font-size:14px;font-weight:700;border-top:2px solid #1f2937;">
        <span>Total</span><span>{{currency invoice.total}}</span>
      </div>
      {{#if invoice.amount_paid}}
      <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;color:#059669;">
        <span>Paid</span><span>-{{currency invoice.amount_paid}}</span>
      </div>
      <div style="display:flex;justify-content:space-between;padding:6px 0;font-size:13px;font-weight:600;color:#dc2626;">
        <span>Balance Due</span><span>{{currency invoice.outstanding_balance}}</span>
      </div>
      {{/if}}
    </div>
  </div>

  {{#if invoice.notes}}
  <div style="margin-top:24px;padding:12px;background:#f9fafb;border-radius:6px;">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#6b7280;">Notes</p>
    <p style="margin:0;font-size:12px;">{{invoice.notes}}</p>
  </div>
  {{/if}}
</div>`;

const INVOICE_FOOTER = `<div style="text-align:center;padding-top:12px;border-top:1px solid #e5e7eb;">
  <p style="margin:0;font-size:10px;color:#9ca3af;">Payment Terms: {{invoice.payment_terms_days}} days | {{firm.name}}</p>
  <p style="margin:4px 0 0;font-size:10px;color:#9ca3af;">{{firm.email}} | {{firm.phone}} | {{firm.website}}</p>
</div>`;

const INVOICE_SAMPLE_DATA = {
  invoice: {
    number: 'INV-0042',
    issue_date: '2026-07-15',
    due_date: '2026-08-14',
    subtotal: 2750,
    tax_amount: 357.5,
    total: 3107.5,
    outstanding_balance: 3107.5,
    amount_paid: 0,
    payment_terms_days: 30,
    notes: 'Thank you for your business. Please remit payment by the due date.',
  },
  client: {
    name: 'Sarah Johnson',
    business_name: 'Johnson Enterprises Pty Ltd',
    address: '45 Collins St, Melbourne VIC 3000',
    email: 'sarah@johnson.com.au',
  },
  line_items: [
    {
      description: 'Individual Tax Return Preparation (FY2026)',
      quantity: 1,
      unit_price: 850,
      line_total: 850,
    },
    { description: 'BAS Preparation - Q4 2026', quantity: 1, unit_price: 450, line_total: 450 },
    { description: 'Financial Statement Review', quantity: 2, unit_price: 500, line_total: 1000 },
    {
      description: 'Tax Advisory Consultation (2 hours)',
      quantity: 2,
      unit_price: 225,
      line_total: 450,
    },
  ],
};

const REPORT_HEADER = `<div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:16px;border-bottom:2px solid #1f2937;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:40px;" alt="Logo" />{{/if}}
    <h2 style="margin:4px 0 0;font-size:14px;color:#6b7280;">{{firm.name}}</h2>
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:20px;color:#1f2937;">{{report.title}}</h1>
    <p style="margin:4px 0 0;font-size:11px;color:#6b7280;">Generated: {{formatDate report.generated_at}}</p>
  </div>
</div>`;

const REPORT_BODY = `<div style="margin-top:20px;">
  <div style="margin-bottom:16px;padding:10px 14px;background:#f0fdf4;border-radius:6px;border:1px solid #bbf7d0;">
    <p style="margin:0;font-size:11px;color:#166534;"><strong>Filters:</strong> {{report.filters}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#166534;"><strong>Total Records:</strong> {{report.total_rows}}</p>
  </div>

  <table style="width:100%;border-collapse:collapse;">
    <thead>
      <tr>
        {{#each columns}}
        <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;color:#6b7280;text-transform:uppercase;border-bottom:2px solid #e5e7eb;background:#f9fafb;">{{this.label}}</th>
        {{/each}}
      </tr>
    </thead>
    <tbody>
      {{#each data}}
      <tr>
        {{#each ../columns}}
        <td style="padding:8px 10px;font-size:11px;border-bottom:1px solid #f3f4f6;">{{lookup .. this.key}}</td>
        {{/each}}
      </tr>
      {{/each}}
    </tbody>
  </table>
</div>`;

const REPORT_FOOTER = `<div style="text-align:center;padding-top:10px;border-top:1px solid #e5e7eb;">
  <p style="margin:0;font-size:9px;color:#9ca3af;">{{firm.name}} | Confidential | Page generated {{formatDate generated_date}}</p>
</div>`;

const REPORT_SAMPLE_DATA = {
  report: {
    title: 'Revenue by Client',
    generated_at: '2026-07-31',
    filters: 'From: 2026-01-01 | To: 2026-06-30',
    total_rows: 5,
  },
  columns: [
    { key: 'client', label: 'Client' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'outstanding', label: 'Outstanding' },
  ],
  data: [
    { client: 'Johnson Enterprises', invoices: 8, revenue: '$24,500.00', outstanding: '$3,200.00' },
    { client: 'Smith & Co', invoices: 5, revenue: '$18,200.00', outstanding: '$0.00' },
    { client: 'Williams Trust', invoices: 3, revenue: '$12,800.00', outstanding: '$4,500.00' },
  ],
};

const STATEMENT_BODY = `<div style="margin-top:24px;">
  <div style="margin-bottom:20px;">
    <h3 style="font-size:12px;color:#6b7280;text-transform:uppercase;margin:0 0 6px;">Statement For</h3>
    <p style="margin:0;font-size:13px;font-weight:600;">{{client.business_name}}</p>
    <p style="margin:2px 0;font-size:12px;">{{client.name}}</p>
    <p style="margin:2px 0;font-size:12px;color:#6b7280;">{{client.address}}</p>
  </div>
  <div style="display:flex;gap:20px;margin-bottom:16px;">
    <div style="padding:8px 14px;background:#f3f4f6;border-radius:4px;font-size:12px;"><strong>Period:</strong> {{formatDate statement.start_date}} - {{formatDate statement.end_date}}</div>
    <div style="padding:8px 14px;background:#f3f4f6;border-radius:4px;font-size:12px;"><strong>Opening Balance:</strong> {{currency statement.opening_balance}}</div>
    <div style="padding:8px 14px;background:#fef3c7;border-radius:4px;font-size:12px;font-weight:600;"><strong>Closing Balance:</strong> {{currency statement.closing_balance}}</div>
  </div>
  <table style="width:100%;border-collapse:collapse;">
    <thead>
      <tr style="background:#f9fafb;">
        <th style="padding:8px 10px;text-align:left;font-size:11px;border-bottom:2px solid #e5e7eb;">Date</th>
        <th style="padding:8px 10px;text-align:left;font-size:11px;border-bottom:2px solid #e5e7eb;">Description</th>
        <th style="padding:8px 10px;text-align:right;font-size:11px;border-bottom:2px solid #e5e7eb;">Debit</th>
        <th style="padding:8px 10px;text-align:right;font-size:11px;border-bottom:2px solid #e5e7eb;">Credit</th>
        <th style="padding:8px 10px;text-align:right;font-size:11px;border-bottom:2px solid #e5e7eb;">Balance</th>
      </tr>
    </thead>
    <tbody>
      {{#each transactions}}
      <tr>
        <td style="padding:8px 10px;font-size:11px;border-bottom:1px solid #f3f4f6;">{{shortDate this.date}}</td>
        <td style="padding:8px 10px;font-size:11px;border-bottom:1px solid #f3f4f6;">{{this.description}}</td>
        <td style="padding:8px 10px;font-size:11px;text-align:right;border-bottom:1px solid #f3f4f6;">{{#if this.debit}}{{currency this.debit}}{{/if}}</td>
        <td style="padding:8px 10px;font-size:11px;text-align:right;border-bottom:1px solid #f3f4f6;">{{#if this.credit}}{{currency this.credit}}{{/if}}</td>
        <td style="padding:8px 10px;font-size:11px;text-align:right;font-weight:600;border-bottom:1px solid #f3f4f6;">{{currency this.balance}}</td>
      </tr>
      {{/each}}
    </tbody>
  </table>
</div>`;

const STATEMENT_SAMPLE_DATA = {
  statement: {
    start_date: '2026-06-01',
    end_date: '2026-06-30',
    opening_balance: 1200,
    closing_balance: 3950,
  },
  client: {
    name: 'Sarah Johnson',
    business_name: 'Johnson Enterprises',
    address: '45 Collins St, Melbourne VIC 3000',
  },
  transactions: [
    { date: '2026-06-05', description: 'Invoice #INV-0038', debit: 1500, credit: 0, balance: 2700 },
    {
      date: '2026-06-12',
      description: 'Payment received - EFT',
      debit: 0,
      credit: 1200,
      balance: 1500,
    },
    { date: '2026-06-20', description: 'Invoice #INV-0041', debit: 2450, credit: 0, balance: 3950 },
  ],
};

// ─── Quote Template ──────────────────────────────────────────────────────────

const QUOTE_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #0ea5e9;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:18px;color:#0f172a;font-weight:700;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#64748b;">{{firm.address_line1}}{{#if firm.address_line2}}, {{firm.address_line2}}{{/if}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.city}} {{firm.province}} {{firm.postal_code}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.phone}} | {{firm.email}}</p>
    {{#if firm.website}}<p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.website}}</p>{{/if}}
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:32px;color:#0ea5e9;letter-spacing:2px;font-weight:700;">QUOTE</h1>
    <p style="margin:10px 0 3px;font-size:12px;color:#0f172a;"><strong>Quote #:</strong> {{quote.number}}</p>
    <p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Date:</strong> {{formatDate quote.issue_date}}</p>
    <p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Valid Until:</strong> {{formatDate quote.valid_until}}</p>
    <div style="margin-top:8px;display:inline-block;padding:3px 10px;background:#f0f9ff;border:1px solid #bae6fd;border-radius:12px;">
      <span style="font-size:11px;color:#0369a1;font-weight:600;text-transform:uppercase;">{{quote.status}}</span>
    </div>
  </div>
</div>`;

const QUOTE_BODY = `<div style="margin-top:24px;">

  <!-- Bill To -->
  <div style="margin-bottom:24px;">
    <h3 style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;margin:0 0 8px;font-weight:600;">Prepared For</h3>
    {{#if client.business_name}}<p style="margin:0;font-size:13px;font-weight:700;color:#0f172a;">{{client.business_name}}</p>{{/if}}
    <p style="margin:2px 0;font-size:12px;color:#334155;">{{client.name}}</p>
    {{#if client.address_line1}}<p style="margin:2px 0;font-size:12px;color:#64748b;">{{client.address_line1}}</p>{{/if}}
    {{#if client.address_line2}}<p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.address_line2}}</p>{{/if}}
    <p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.city}} {{client.province}} {{client.postal_code}}</p>
    {{#if client.country}}<p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.country}}</p>{{/if}}
    {{#if client.email}}<p style="margin:4px 0 0;font-size:12px;color:#64748b;">{{client.email}}</p>{{/if}}
  </div>

  <!-- Line Items Table -->
  <table style="width:100%;border-collapse:collapse;margin-top:8px;">
    <thead>
      <tr style="background:#f8fafc;">
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">#</th>
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">Description</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">Hrs</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">Rate</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">Disc.</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">Tax</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.5px;">Amount</th>
      </tr>
    </thead>
    <tbody>
      {{#each line_items}}
      <tr>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;color:#94a3b8;">{{inc @index}}</td>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;color:#1e293b;">{{this.description}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f1f5f9;color:#334155;">{{this.hours}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;color:#334155;">{{currency this.charges}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f1f5f9;color:#334155;">
          {{#if this.discount_value}}{{this.discount_value}}{{this.discount_type}}{{else}}&mdash;{{/if}}
        </td>
        <td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f1f5f9;color:#334155;">
          {{#if this.tax_rate}}{{this.tax_rate}}%{{else}}&mdash;{{/if}}
        </td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;font-weight:600;color:#0f172a;">{{currency this.line_total}}</td>
      </tr>
      {{/each}}
    </tbody>
  </table>

  <!-- Totals -->
  <div style="display:flex;justify-content:flex-end;margin-top:16px;">
    <div style="width:260px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">Gross Total</span>
        <span style="color:#334155;">{{currency quote.gross_total}}</span>
      </div>
      {{#if quote.discount_amount}}
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;background:#fefce8;">
        <span style="color:#92400e;">Discount</span>
        <span style="color:#d97706;font-weight:600;">-{{currency quote.discount_amount}}</span>
      </div>
      {{/if}}
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">Subtotal</span>
        <span style="color:#334155;">{{currency quote.subtotal}}</span>
      </div>
      {{#each tax_breakdown}}
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">{{this.tax_type}} ({{this.tax_rate}}%)</span>
        <span style="color:#334155;">{{currency this.tax_amount}}</span>
      </div>
      {{/each}}
      {{#if quote.rounding_amount}}
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">Rounding</span>
        <span style="color:#334155;">{{currency quote.rounding_amount}}</span>
      </div>
      {{/if}}
      <div style="display:flex;justify-content:space-between;padding:12px 14px;font-size:15px;font-weight:700;background:#0ea5e9;">
        <span style="color:#ffffff;">Net Total</span>
        <span style="color:#ffffff;">{{currency quote.total}}</span>
      </div>
    </div>
  </div>

  {{#if quote.notes}}
  <div style="margin-top:24px;padding:14px;background:#f0f9ff;border-left:4px solid #0ea5e9;border-radius:0 6px 6px 0;">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#0369a1;text-transform:uppercase;letter-spacing:0.5px;">Notes</p>
    <p style="margin:0;font-size:12px;color:#334155;">{{quote.notes}}</p>
  </div>
  {{/if}}

</div>`;

const QUOTE_FOOTER = `<div style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid #e2e8f0;">
  <p style="margin:0;font-size:10px;color:#94a3b8;">This quote is valid until {{formatDate quote.valid_until}}. Prices are subject to change after this date.</p>
  <p style="margin:0;font-size:10px;color:#94a3b8;">{{firm.name}} | {{firm.email}}</p>
</div>`;

const QUOTE_CSS = `
  body { font-family: 'Helvetica Neue', Arial, sans-serif; }
  table { page-break-inside: avoid; }
`;

const QUOTE_SAMPLE_DATA = {
  quote: {
    number: 'QT-00024',
    status: 'sent',
    issue_date: '2026-08-27',
    valid_until: '2026-09-26',
    gross_total: 22500.0,
    discount_amount: 1125.0,
    subtotal: 21375.0,
    tax_amount: 7053.75,
    rounding_amount: 1.25,
    total: 28430.0,
    notes:
      'Thank you for your interest. Please review this quote and contact us if you have any questions.',
  },
  client: {
    name: 'khurram bilal khurram',
    business_name: 'khu',
    client_code: 'CLI-0024',
    address_line1: '123 Main Street',
    city: 'Toronto',
    province: 'ON',
    postal_code: 'M5H 2N2',
    country: 'Canada',
    email: 'khurram@example.com',
  },
  line_items: [
    {
      description: 'service2',
      hours: 15,
      charges: 1500,
      discount_type: '%',
      discount_value: 5,
      discount_amount: 1125,
      tax_rate: 33,
      tax_type: 'tax2',
      line_total: 21375,
    },
  ],
  tax_breakdown: [{ tax_type: 'tax2', tax_rate: 33, taxable_amount: 21375, tax_amount: 7053.75 }],
  firm: {
    name: 'Practice Entity 1',
    address_line1: '250 Consumers Road',
    address_line2: 'Suite 501',
    city: 'North York',
    province: 'ON',
    postal_code: 'M2J 4V6',
    phone: '(416) 123-4567',
    email: 'info@practiceentity1.ca',
    website: 'www.practiceentity1.ca',
    logo_url: null,
  },
};

// ─── Credit Note Template ─────────────────────────────────────────────────────

const CREDIT_NOTE_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #10b981;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:18px;color:#0f172a;font-weight:700;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#64748b;">{{firm.address_line1}}{{#if firm.address_line2}}, {{firm.address_line2}}{{/if}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.city}} {{firm.province}} {{firm.postal_code}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.phone}} | {{firm.email}}</p>
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:30px;color:#10b981;letter-spacing:1px;font-weight:700;">CREDIT NOTE</h1>
    <p style="margin:10px 0 3px;font-size:12px;"><strong>Credit Note #:</strong> {{credit_note.number}}</p>
    <p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Date:</strong> {{formatDate credit_note.transaction_date}}</p>
    {{#if invoice.number}}<p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Ref Invoice #:</strong> {{invoice.number}}</p>{{/if}}
    <div style="margin-top:8px;display:inline-block;padding:3px 10px;background:#f0fdf4;border:1px solid #86efac;border-radius:12px;">
      <span style="font-size:11px;color:#16a34a;font-weight:600;text-transform:uppercase;">{{credit_note.status}}</span>
    </div>
  </div>
</div>`;

const CREDIT_NOTE_BODY = `<div style="margin-top:24px;">

  <!-- Issued To -->
  <div style="margin-bottom:24px;">
    <h3 style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;margin:0 0 8px;font-weight:600;">Issued To</h3>
    {{#if client.business_name}}<p style="margin:0;font-size:13px;font-weight:700;color:#0f172a;">{{client.business_name}}</p>{{/if}}
    <p style="margin:2px 0;font-size:12px;color:#334155;">{{client.name}}</p>
    {{#if client.address_line1}}<p style="margin:2px 0;font-size:12px;color:#64748b;">{{client.address_line1}}</p>{{/if}}
    <p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.city}} {{client.province}} {{client.postal_code}}</p>
    {{#if client.email}}<p style="margin:4px 0 0;font-size:12px;color:#64748b;">{{client.email}}</p>{{/if}}
  </div>

  <!-- Credit Summary Box -->
  <div style="background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:20px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:center;">
    <div>
      <p style="margin:0;font-size:12px;color:#15803d;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Total Credit Issued</p>
      <p style="margin:6px 0 0;font-size:28px;font-weight:700;color:#15803d;">{{currency credit_note.amount}}</p>
    </div>
    {{#if invoice.number}}
    <div style="text-align:right;">
      <p style="margin:0;font-size:11px;color:#64748b;">Applied to Invoice</p>
      <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#0f172a;">#{{invoice.number}}</p>
      {{#if invoice.issue_date}}<p style="margin:2px 0 0;font-size:11px;color:#64748b;">Issued {{formatDate invoice.issue_date}}</p>{{/if}}
    </div>
    {{/if}}
  </div>

  <!-- Reason -->
  {{#if credit_note.reason}}
  <div style="margin-bottom:24px;padding:14px;background:#f8fafc;border-left:4px solid #10b981;border-radius:0 6px 6px 0;">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#10b981;text-transform:uppercase;letter-spacing:0.5px;">Reason for Credit</p>
    <p style="margin:0;font-size:12px;color:#334155;">{{credit_note.reason}}</p>
  </div>
  {{/if}}

  <!-- Original Invoice Line Items (if available) -->
  {{#if line_items.length}}
  <div style="margin-top:8px;">
    <p style="margin:0 0 10px;font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">Original Invoice Items</p>
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr style="background:#f8fafc;">
          <th style="padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Description</th>
          <th style="padding:8px 12px;text-align:center;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Qty</th>
          <th style="padding:8px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Rate</th>
          <th style="padding:8px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Amount</th>
        </tr>
      </thead>
      <tbody>
        {{#each line_items}}
        <tr>
          <td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;color:#1e293b;">{{this.description}}</td>
          <td style="padding:8px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f1f5f9;">{{this.quantity}}</td>
          <td style="padding:8px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;">{{currency this.unit_price}}</td>
          <td style="padding:8px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;font-weight:600;">{{currency this.line_total}}</td>
        </tr>
        {{/each}}
      </tbody>
    </table>
    {{#each tax_breakdown}}
    <div style="display:flex;justify-content:flex-end;padding:6px 12px;font-size:12px;color:#64748b;">
      <span style="margin-right:40px;">{{this.tax_type}} ({{this.tax_rate}}%)</span>
      <span>{{currency this.tax_amount}}</span>
    </div>
    {{/each}}
  </div>
  {{/if}}

</div>`;

const CREDIT_NOTE_FOOTER = `<div style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid #e2e8f0;">
  <p style="margin:0;font-size:10px;color:#94a3b8;">This credit note has been issued by {{firm.name}} on {{formatDate generated_date}}.</p>
  <p style="margin:0;font-size:10px;color:#94a3b8;">{{firm.email}} | {{firm.phone}}</p>
</div>`;

const CREDIT_NOTE_SAMPLE_DATA = {
  credit_note: {
    number: 'CN-00012',
    status: 'issued',
    amount: 850.0,
    reason: 'Overcharge on tax preparation fee — corrected rate applied.',
    transaction_date: '2026-08-27',
    invoice_id: 'inv-001',
  },
  invoice: {
    number: 'INV-0038',
    issue_date: '2026-08-01',
    due_date: '2026-08-31',
    subtotal: 2750,
    tax_amount: 357.5,
    total: 3107.5,
    outstanding_balance: 3107.5,
    status: 'sent',
  },
  client: {
    name: 'Sarah Johnson',
    business_name: 'Johnson Enterprises',
    client_code: 'CLI-0012',
    address_line1: '45 Collins St',
    city: 'Toronto',
    province: 'ON',
    postal_code: 'M5J 2T3',
    country: 'Canada',
    email: 'sarah@johnson.com.au',
  },
  line_items: [
    {
      description: 'T1 Personal Tax Return',
      quantity: 1,
      unit_price: 850,
      tax_rate: 13,
      tax_type: 'HST',
      line_total: 850,
    },
    {
      description: 'BAS Preparation Q4',
      quantity: 1,
      unit_price: 450,
      tax_rate: 13,
      tax_type: 'HST',
      line_total: 450,
    },
  ],
  tax_breakdown: [{ tax_type: 'HST', tax_rate: 13, taxable_amount: 1300, tax_amount: 169 }],
  firm: {
    name: 'Practice Entity 1',
    address_line1: '250 Consumers Road',
    city: 'North York',
    province: 'ON',
    postal_code: 'M2J 4V6',
    phone: '(416) 123-4567',
    email: 'info@practiceentity1.ca',
    logo_url: null,
  },
};

// ─── Debit Note Template ──────────────────────────────────────────────────────

const DEBIT_NOTE_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #f59e0b;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:18px;color:#0f172a;font-weight:700;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#64748b;">{{firm.address_line1}}{{#if firm.address_line2}}, {{firm.address_line2}}{{/if}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.city}} {{firm.province}} {{firm.postal_code}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.phone}} | {{firm.email}}</p>
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:30px;color:#f59e0b;letter-spacing:1px;font-weight:700;">DEBIT NOTE</h1>
    <p style="margin:10px 0 3px;font-size:12px;"><strong>Debit Note #:</strong> {{debit_note.number}}</p>
    <p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Date:</strong> {{formatDate debit_note.issue_date}}</p>
    {{#if debit_note.due_date}}<p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Due:</strong> {{formatDate debit_note.due_date}}</p>{{/if}}
    <div style="margin-top:8px;display:inline-block;padding:3px 10px;background:#fffbeb;border:1px solid #fcd34d;border-radius:12px;">
      <span style="font-size:11px;color:#b45309;font-weight:600;text-transform:uppercase;">{{debit_note.status}}</span>
    </div>
  </div>
</div>`;

const DEBIT_NOTE_BODY = `<div style="margin-top:24px;">

  <!-- Billed To -->
  <div style="margin-bottom:24px;">
    <h3 style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;margin:0 0 8px;font-weight:600;">Billed To</h3>
    {{#if client.business_name}}<p style="margin:0;font-size:13px;font-weight:700;color:#0f172a;">{{client.business_name}}</p>{{/if}}
    <p style="margin:2px 0;font-size:12px;color:#334155;">{{client.name}}</p>
    {{#if client.address_line1}}<p style="margin:2px 0;font-size:12px;color:#64748b;">{{client.address_line1}}</p>{{/if}}
    <p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.city}} {{client.province}} {{client.postal_code}}</p>
    {{#if client.email}}<p style="margin:4px 0 0;font-size:12px;color:#64748b;">{{client.email}}</p>{{/if}}
  </div>

  <!-- Amount Due Box -->
  <div style="background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:20px;margin-bottom:24px;">
    <p style="margin:0;font-size:12px;color:#92400e;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Additional Amount Due</p>
    <p style="margin:8px 0 0;font-size:32px;font-weight:700;color:#92400e;">{{currency debit_note.amount}}</p>
    {{#if debit_note.service_type_code}}<p style="margin:6px 0 0;font-size:12px;color:#78350f;">Service: {{debit_note.service_type_code}}</p>{{/if}}
  </div>

  <!-- Reason -->
  {{#if debit_note.reason}}
  <div style="margin-bottom:24px;padding:14px;background:#f8fafc;border-left:4px solid #f59e0b;border-radius:0 6px 6px 0;">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#b45309;text-transform:uppercase;letter-spacing:0.5px;">Reason / Description</p>
    <p style="margin:0;font-size:12px;color:#334155;">{{debit_note.reason}}</p>
  </div>
  {{/if}}

  <!-- Totals -->
  <div style="display:flex;justify-content:flex-end;margin-top:16px;">
    <div style="width:240px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">Subtotal</span>
        <span>{{currency invoice.subtotal}}</span>
      </div>
      {{#if invoice.tax_amount}}
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">Tax</span>
        <span>{{currency invoice.tax_amount}}</span>
      </div>
      {{/if}}
      <div style="display:flex;justify-content:space-between;padding:12px 14px;font-size:15px;font-weight:700;background:#f59e0b;">
        <span style="color:#ffffff;">Total Due</span>
        <span style="color:#ffffff;">{{currency invoice.total}}</span>
      </div>
    </div>
  </div>

</div>`;

const DEBIT_NOTE_FOOTER = `<div style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid #e2e8f0;">
  <p style="margin:0;font-size:10px;color:#94a3b8;">Payment is due by {{formatDate debit_note.due_date}}. Please contact us if you have any questions.</p>
  <p style="margin:0;font-size:10px;color:#94a3b8;">{{firm.name}} | {{firm.email}}</p>
</div>`;

const DEBIT_NOTE_SAMPLE_DATA = {
  debit_note: {
    number: 'DN-00008',
    status: 'issued',
    amount: 450.0,
    reason: 'Additional consulting hours not included in original invoice — 3hrs at $150/hr.',
    issue_date: '2026-08-27',
    due_date: '2026-09-10',
    service_type_code: 'TAX-CONSULT',
    converted_invoice_id: null,
  },
  invoice: {
    number: 'DN-00008',
    issue_date: '2026-08-27',
    due_date: '2026-09-10',
    subtotal: 450,
    tax_amount: 0,
    total: 450,
    outstanding_balance: 450,
    status: 'issued',
    notes: '',
  },
  client: {
    name: 'Michael Chen',
    business_name: 'Chen Holdings Inc.',
    client_code: 'CLI-0008',
    address_line1: '88 King Street W',
    city: 'Toronto',
    province: 'ON',
    postal_code: 'M5H 1M2',
    country: 'Canada',
    email: 'michael@chenholdings.ca',
  },
  line_items: [],
  tax_breakdown: [],
  firm: {
    name: 'Practice Entity 1',
    address_line1: '250 Consumers Road',
    city: 'North York',
    province: 'ON',
    postal_code: 'M2J 4V6',
    phone: '(416) 123-4567',
    email: 'info@practiceentity1.ca',
    logo_url: null,
  },
};

// ─── Write-Off Template ───────────────────────────────────────────────────────

const WRITE_OFF_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #8b5cf6;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:18px;color:#0f172a;font-weight:700;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#64748b;">{{firm.address_line1}}{{#if firm.address_line2}}, {{firm.address_line2}}{{/if}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.city}} {{firm.province}} {{firm.postal_code}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.phone}} | {{firm.email}}</p>
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:30px;color:#8b5cf6;letter-spacing:1px;font-weight:700;">WRITE-OFF</h1>
    <p style="margin:10px 0 3px;font-size:12px;"><strong>Write-Off #:</strong> {{write_off.number}}</p>
    <p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Date:</strong> {{formatDate write_off.transaction_date}}</p>
    {{#if invoice.number}}<p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Ref Invoice #:</strong> {{invoice.number}}</p>{{/if}}
    <div style="margin-top:8px;display:inline-block;padding:3px 10px;background:#f5f3ff;border:1px solid #c4b5fd;border-radius:12px;">
      <span style="font-size:11px;color:#7c3aed;font-weight:600;text-transform:uppercase;">{{write_off.status}}</span>
    </div>
  </div>
</div>`;

const WRITE_OFF_BODY = `<div style="margin-top:24px;">

  <!-- Client -->
  <div style="margin-bottom:24px;">
    <h3 style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;margin:0 0 8px;font-weight:600;">Client</h3>
    {{#if client.business_name}}<p style="margin:0;font-size:13px;font-weight:700;color:#0f172a;">{{client.business_name}}</p>{{/if}}
    <p style="margin:2px 0;font-size:12px;color:#334155;">{{client.name}}</p>
    {{#if client.address_line1}}<p style="margin:2px 0;font-size:12px;color:#64748b;">{{client.address_line1}}</p>{{/if}}
    <p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.city}} {{client.province}} {{client.postal_code}}</p>
    {{#if client.email}}<p style="margin:4px 0 0;font-size:12px;color:#64748b;">{{client.email}}</p>{{/if}}
  </div>

  <!-- Write-Off Summary -->
  <div style="background:#f5f3ff;border:1px solid #c4b5fd;border-radius:8px;padding:20px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:center;">
    <div>
      <p style="margin:0;font-size:12px;color:#7c3aed;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Amount Written Off</p>
      <p style="margin:6px 0 0;font-size:28px;font-weight:700;color:#7c3aed;">{{currency write_off.amount}}</p>
      {{#if write_off.service_type_code}}<p style="margin:6px 0 0;font-size:12px;color:#6d28d9;">Service: {{write_off.service_type_code}}</p>{{/if}}
    </div>
    {{#if invoice.number}}
    <div style="text-align:right;">
      <p style="margin:0;font-size:11px;color:#64748b;">Written Off From Invoice</p>
      <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#0f172a;">#{{invoice.number}}</p>
      {{#if invoice.total}}<p style="margin:2px 0 0;font-size:11px;color:#64748b;">Invoice Total: {{currency invoice.total}}</p>{{/if}}
    </div>
    {{/if}}
  </div>

  <!-- Reason -->
  {{#if write_off.reason}}
  <div style="margin-bottom:24px;padding:14px;background:#f8fafc;border-left:4px solid #8b5cf6;border-radius:0 6px 6px 0;">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#7c3aed;text-transform:uppercase;letter-spacing:0.5px;">Reason for Write-Off</p>
    <p style="margin:0;font-size:12px;color:#334155;">{{write_off.reason}}</p>
  </div>
  {{/if}}

  <!-- Original Invoice Line Items (if available) -->
  {{#if line_items.length}}
  <div>
    <p style="margin:0 0 10px;font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">Original Invoice Items</p>
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr style="background:#f8fafc;">
          <th style="padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Description</th>
          <th style="padding:8px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Tax Rate</th>
          <th style="padding:8px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;">Amount</th>
        </tr>
      </thead>
      <tbody>
        {{#each line_items}}
        <tr>
          <td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">{{this.description}}</td>
          <td style="padding:8px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;">{{this.tax_rate}}%</td>
          <td style="padding:8px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;font-weight:600;">{{currency this.line_total}}</td>
        </tr>
        {{/each}}
      </tbody>
    </table>
  </div>
  {{/if}}

</div>`;

const WRITE_OFF_FOOTER = `<div style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid #e2e8f0;">
  <p style="margin:0;font-size:10px;color:#94a3b8;">Write-off processed on {{formatDate write_off.transaction_date}} by {{firm.name}}.</p>
  <p style="margin:0;font-size:10px;color:#94a3b8;">{{firm.email}} | {{firm.phone}}</p>
</div>`;

const WRITE_OFF_SAMPLE_DATA = {
  write_off: {
    number: 'WO-00005',
    status: 'approved',
    amount: 1200.0,
    reason: 'Client hardship — balance waived after payment arrangement failed.',
    transaction_date: '2026-08-27',
    service_type_code: 'TAX-PREP',
    invoice_id: 'inv-001',
    voided_at: null,
    voided_reason: null,
  },
  invoice: {
    number: 'INV-0031',
    issue_date: '2026-06-01',
    due_date: '2026-07-01',
    subtotal: 1200,
    tax_amount: 156,
    total: 1356,
    outstanding_balance: 1200,
    status: 'overdue',
  },
  client: {
    name: 'Robert Williams',
    business_name: 'Williams Trust',
    client_code: 'CLI-0005',
    address_line1: '10 Bay Street',
    city: 'Toronto',
    province: 'ON',
    postal_code: 'M5J 2R8',
    country: 'Canada',
    email: 'r.williams@trust.ca',
  },
  line_items: [
    {
      description: 'Corporate Tax Return Preparation FY2025',
      quantity: 1,
      unit_price: 1200,
      tax_rate: 13,
      tax_type: 'HST',
      line_total: 1200,
    },
  ],
  tax_breakdown: [{ tax_type: 'HST', tax_rate: 13, taxable_amount: 1200, tax_amount: 156 }],
  firm: {
    name: 'Practice Entity 1',
    address_line1: '250 Consumers Road',
    city: 'North York',
    province: 'ON',
    postal_code: 'M2J 4V6',
    phone: '(416) 123-4567',
    email: 'info@practiceentity1.ca',
    logo_url: null,
  },
};

// ─── Advance Template ─────────────────────────────────────────────────────────

const ADVANCE_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #0891b2;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:18px;color:#0f172a;font-weight:700;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#64748b;">{{firm.address_line1}}{{#if firm.address_line2}}, {{firm.address_line2}}{{/if}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.city}} {{firm.province}} {{firm.postal_code}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#64748b;">{{firm.phone}} | {{firm.email}}</p>
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:30px;color:#0891b2;letter-spacing:1px;font-weight:700;">ADVANCE RECEIPT</h1>
    <p style="margin:10px 0 3px;font-size:12px;"><strong>Advance #:</strong> {{advance.number}}</p>
    <p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Date:</strong> {{formatDate advance.transaction_date}}</p>
    {{#if advance.due_date}}<p style="margin:3px 0;font-size:12px;color:#475569;"><strong>Due:</strong> {{formatDate advance.due_date}}</p>{{/if}}
    <div style="margin-top:8px;display:inline-block;padding:3px 10px;background:#ecfeff;border:1px solid #67e8f9;border-radius:12px;">
      <span style="font-size:11px;color:#0e7490;font-weight:600;text-transform:uppercase;">{{advance.status}}</span>
    </div>
  </div>
</div>`;

const ADVANCE_BODY = `<div style="margin-top:24px;">

  <!-- Received From -->
  <div style="margin-bottom:24px;">
    <h3 style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;margin:0 0 8px;font-weight:600;">Received From</h3>
    {{#if client.business_name}}<p style="margin:0;font-size:13px;font-weight:700;color:#0f172a;">{{client.business_name}}</p>{{/if}}
    <p style="margin:2px 0;font-size:12px;color:#334155;">{{client.name}}</p>
    {{#if client.address_line1}}<p style="margin:2px 0;font-size:12px;color:#64748b;">{{client.address_line1}}</p>{{/if}}
    <p style="margin:1px 0;font-size:12px;color:#64748b;">{{client.city}} {{client.province}} {{client.postal_code}}</p>
    {{#if client.email}}<p style="margin:4px 0 0;font-size:12px;color:#64748b;">{{client.email}}</p>{{/if}}
  </div>

  <!-- Balance Summary Cards -->
  <div style="display:flex;gap:12px;margin-bottom:24px;">
    <div style="flex:1;padding:16px;background:#ecfeff;border:1px solid #67e8f9;border-radius:8px;text-align:center;">
      <p style="margin:0;font-size:11px;color:#0e7490;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Total Advance</p>
      <p style="margin:6px 0 0;font-size:22px;font-weight:700;color:#0891b2;">{{currency advance.total}}</p>
    </div>
    <div style="flex:1;padding:16px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;text-align:center;">
      <p style="margin:0;font-size:11px;color:#15803d;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Available Balance</p>
      <p style="margin:6px 0 0;font-size:22px;font-weight:700;color:#16a34a;">{{currency advance.available_balance}}</p>
    </div>
    {{#if advance.amount_used}}
    <div style="flex:1;padding:16px;background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;text-align:center;">
      <p style="margin:0;font-size:11px;color:#c2410c;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Amount Used</p>
      <p style="margin:6px 0 0;font-size:22px;font-weight:700;color:#ea580c;">{{currency advance.amount_used}}</p>
    </div>
    {{/if}}
  </div>

  <!-- Line Items -->
  {{#if line_items.length}}
  <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
    <thead>
      <tr style="background:#f8fafc;">
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;">#</th>
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;">Description</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;">Qty</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;">Rate</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;">Tax</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#64748b;border-bottom:2px solid #e2e8f0;text-transform:uppercase;">Amount</th>
      </tr>
    </thead>
    <tbody>
      {{#each line_items}}
      <tr>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;color:#94a3b8;">{{inc @index}}</td>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;color:#1e293b;">{{this.description}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f1f5f9;">{{this.quantity}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;">{{currency this.unit_price}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f1f5f9;">{{#if this.tax_rate}}{{this.tax_rate}}%{{else}}&mdash;{{/if}}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f1f5f9;font-weight:600;">{{currency this.line_total}}</td>
      </tr>
      {{/each}}
    </tbody>
  </table>

  <!-- Totals -->
  <div style="display:flex;justify-content:flex-end;margin-top:8px;">
    <div style="width:240px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">Subtotal</span><span>{{currency advance.subtotal}}</span>
      </div>
      {{#each tax_breakdown}}
      <div style="display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;border-bottom:1px solid #f1f5f9;">
        <span style="color:#64748b;">{{this.tax_type}} ({{this.tax_rate}}%)</span>
        <span>{{currency this.tax_amount}}</span>
      </div>
      {{/each}}
      <div style="display:flex;justify-content:space-between;padding:12px 14px;font-size:15px;font-weight:700;background:#0891b2;">
        <span style="color:#ffffff;">Total</span>
        <span style="color:#ffffff;">{{currency advance.total}}</span>
      </div>
    </div>
  </div>
  {{/if}}

  <!-- Notes -->
  {{#if advance.notes}}
  <div style="margin-top:20px;padding:14px;background:#ecfeff;border-left:4px solid #0891b2;border-radius:0 6px 6px 0;">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#0e7490;text-transform:uppercase;letter-spacing:0.5px;">Notes</p>
    <p style="margin:0;font-size:12px;color:#334155;">{{advance.notes}}</p>
  </div>
  {{/if}}

</div>`;

const ADVANCE_FOOTER = `<div style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid #e2e8f0;">
  <p style="margin:0;font-size:10px;color:#94a3b8;">This advance receipt was issued by {{firm.name}} on {{formatDate generated_date}}.</p>
  <p style="margin:0;font-size:10px;color:#94a3b8;">{{firm.email}} | {{firm.phone}}</p>
</div>`;

const ADVANCE_SAMPLE_DATA = {
  advance: {
    number: 'ADV-00003',
    status: 'active',
    subtotal: 5000.0,
    tax_amount: 650.0,
    total: 5650.0,
    amount: 5650.0,
    available_balance: 4000.0,
    amount_used: 1650.0,
    reason: 'Advance retainer for FY2027 tax planning and corporate filings.',
    notes: 'Advance retainer for FY2027 tax planning and corporate filings.',
    transaction_date: '2026-08-01',
    due_date: null,
    received_at: '2026-08-01',
    job_reference: null,
  },
  client: {
    name: 'Anna Petrova',
    business_name: 'Petrova Holdings Ltd.',
    client_code: 'CLI-0003',
    address_line1: '200 Front Street W',
    city: 'Toronto',
    province: 'ON',
    postal_code: 'M5V 3K2',
    country: 'Canada',
    email: 'anna@petrovaholdings.com',
  },
  line_items: [
    {
      description: 'Tax Planning Retainer — FY2027',
      quantity: 1,
      unit_price: 3000,
      tax_rate: 13,
      tax_type: 'HST',
      line_total: 3000,
    },
    {
      description: 'Corporate Filing Retainer',
      quantity: 1,
      unit_price: 2000,
      tax_rate: 13,
      tax_type: 'HST',
      line_total: 2000,
    },
  ],
  tax_breakdown: [{ tax_type: 'HST', tax_rate: 13, taxable_amount: 5000, tax_amount: 650 }],
  firm: {
    name: 'Practice Entity 1',
    address_line1: '250 Consumers Road',
    city: 'North York',
    province: 'ON',
    postal_code: 'M2J 4V6',
    phone: '(416) 123-4567',
    email: 'info@practiceentity1.ca',
    logo_url: null,
  },
};

// ─── EPC Template ────────────────────────────────────────────────────────────

const EPC_HEADER = `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:2px solid #2d3748;">
  <div>
    {{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:55px;margin-bottom:8px;" alt="Logo" />{{/if}}
    <h2 style="margin:0;font-size:16px;color:#1a202c;font-weight:700;">{{firm.name}}</h2>
    <p style="margin:4px 0 0;font-size:11px;color:#4a5568;">{{firm.address_line1}}</p>
    {{#if firm.address_line2}}<p style="margin:2px 0 0;font-size:11px;color:#4a5568;">{{firm.address_line2}}</p>{{/if}}
    <p style="margin:2px 0 0;font-size:11px;color:#4a5568;">{{firm.city}} {{firm.province}} {{firm.postal_code}}</p>
    <p style="margin:2px 0 0;font-size:11px;color:#4a5568;">{{firm.phone}} | {{firm.email}}</p>
    {{#if firm.website}}<p style="margin:2px 0 0;font-size:11px;color:#4a5568;">{{firm.website}}</p>{{/if}}
  </div>
  <div style="text-align:right;">
    <h1 style="margin:0;font-size:26px;color:#2d3748;font-weight:700;letter-spacing:0.5px;">INVOICE</h1>
  </div>
</div>`;

const EPC_BODY = `<div style="margin-top:20px;">
  <!-- Invoice Date -->
  <p style="margin:0 0 16px;font-size:12px;color:#4a5568;">{{formatDate invoice.issue_date}}</p>

  <!-- Client Address Block -->
  <div style="margin-bottom:20px;font-size:12px;line-height:1.6;">
    {{#if client.business_name}}<p style="margin:0;font-weight:600;">{{client.business_name}}</p>{{/if}}
    <p style="margin:0;">{{client.name}}</p>
    {{#if client.address_line1}}<p style="margin:0;">{{client.address_line1}}</p>{{/if}}
    {{#if client.address_line2}}<p style="margin:0;">{{client.address_line2}}</p>{{/if}}
    <p style="margin:0;">{{client.city}} {{client.province}} {{client.postal_code}}</p>
    {{#if client.country}}<p style="margin:0;">{{client.country}}</p>{{/if}}
  </div>

  <!-- Client No & Invoice No -->
  <table style="width:100%;margin-bottom:20px;border-collapse:collapse;">
    <tr>
      <td style="padding:8px 12px;font-size:12px;border:1px solid #e2e8f0;background:#f7fafc;">Client No.: <strong>{{client.client_code}}</strong></td>
      <td style="padding:8px 12px;font-size:12px;border:1px solid #e2e8f0;background:#f7fafc;text-align:right;">Invoice: <strong>{{invoice.number}}</strong></td>
    </tr>
  </table>

  <!-- Line Items (description only, no per-item price) -->
  <table style="width:100%;border-collapse:collapse;margin-bottom:4px;">
    <thead>
      <tr>
        <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#4a5568;border-bottom:2px solid #e2e8f0;background:#f7fafc;">Description</th>
      </tr>
    </thead>
    <tbody>
      {{#each line_items}}
      <tr>
        <td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">{{this.description}}</td>
      </tr>
      {{/each}}
    </tbody>
  </table>

  <!-- Totals -->
  <table style="width:100%;border-collapse:collapse;margin-top:8px;">
    <tr>
      <td style="padding:6px 12px;font-size:12px;text-align:right;border-bottom:1px solid #e2e8f0;">Subtotal</td>
      <td style="padding:6px 12px;font-size:12px;text-align:right;width:120px;border-bottom:1px solid #e2e8f0;">{{currency invoice.subtotal}}</td>
    </tr>
    {{#each tax_breakdown}}
    <tr>
      <td style="padding:6px 12px;font-size:12px;text-align:right;border-bottom:1px solid #e2e8f0;">{{this.tax_type}} ({{this.tax_rate}}%)</td>
      <td style="padding:6px 12px;font-size:12px;text-align:right;width:120px;border-bottom:1px solid #e2e8f0;">{{currency this.tax_amount}}</td>
    </tr>
    {{/each}}
    <tr>
      <td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:right;border-top:2px solid #2d3748;">Total</td>
      <td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:right;width:120px;border-top:2px solid #2d3748;">{{currency invoice.total}}</td>
    </tr>
  </table>

  <!-- Terms -->
  <p style="margin:20px 0 0;font-size:12px;color:#4a5568;">Invoice due upon receipt.</p>

  <!-- Note -->
  <div style="margin-top:16px;">
    <p style="margin:0 0 4px;font-size:12px;font-weight:700;">Note:</p>
    <ul style="margin:0;padding-left:20px;font-size:11px;color:#4a5568;line-height:1.6;">
      <li>Edmonds Professional Corporation (EPC Accounting) has merged with GTA Accounting Professional Corporation effective Jan 4, 2022, and now operating as GTA Accounting Professional Corporation.</li>
    </ul>
  </div>

  <!-- Payment Options -->
  <div style="margin-top:16px;">
    <p style="margin:0 0 4px;font-size:12px;font-weight:700;">Payment Options:</p>
    <ul style="margin:0;padding-left:20px;font-size:11px;color:#4a5568;line-height:1.8;">
      <li>Interac e-Transfer: Send invoice payments to <strong>payments@gtaaccountinggroup.com</strong> (please mention invoice number in message, no password needed)</li>
      <li>Direct Deposit / EFT – Bank Info: TD Bank Canada. Transit 22402 Account Number: 5013986 (email deposit receipt)</li>
      <li>Cheque: GTA Accounting Professional Corporation, 250 Consumers Road, Suite 501, North York ON M2J 4V6 Canada</li>
      <li>Credit Card: https://forms.gtaaccounting.ca/payment (3% credit card fees apply)</li>
    </ul>
  </div>
</div>`;

const EPC_FOOTER = `<div style="margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;text-align:center;">
  <p style="margin:0;font-size:11px;font-weight:600;color:#4a5568;"><strong>HST Number:</strong> 70112 6294 RT0001</p>
  <p style="margin:6px 0 0;font-size:10px;color:#9ca3af;">{{firm.name}} | {{firm.phone}} | {{firm.email}}</p>
</div>`;

const EPC_CSS = `
  body { font-family: 'Calibri', 'Segoe UI', Arial, sans-serif; }
  table { page-break-inside: avoid; }
`;

const EPC_SAMPLE_DATA = {
  invoice: {
    number: 'EPC-2026-0015',
    issue_date: '2026-07-15',
    due_date: '2026-07-15',
    subtotal: 1500,
    tax_amount: 195,
    total: 1695,
    outstanding_balance: 1695,
    amount_paid: 0,
    payment_terms_days: 0,
    notes: '',
  },
  client: {
    name: 'John Smith',
    business_name: 'Smith Holdings Inc.',
    client_code: 'GTA-1042',
    address_line1: '123 Bay Street',
    address_line2: 'Suite 400',
    city: 'Toronto',
    province: 'ON',
    postal_code: 'M5J 2T3',
    country: 'Canada',
    email: 'john@smithholdings.ca',
  },
  line_items: [
    {
      description: 'T1 Personal Tax Return Preparation – 2025 Tax Year',
      quantity: 1,
      unit_price: 850,
      line_total: 850,
    },
    {
      description: 'T2 Corporate Tax Return – Smith Holdings Inc.',
      quantity: 1,
      unit_price: 650,
      line_total: 650,
    },
  ],
  tax_breakdown: [{ tax_type: 'HST', tax_rate: 13, taxable_amount: 1500, tax_amount: 195 }],
  firm: {
    name: 'GTA Accounting Professional Corporation',
    address_line1: '250 Consumers Road',
    address_line2: 'Suite 501',
    city: 'North York',
    province: 'ON',
    postal_code: 'M2J 4V6',
    phone: '(416) 123-4567',
    email: 'info@gtaaccountinggroup.com',
    website: 'www.gtaaccounting.ca',
    logo_url: null,
  },
};

const STARTERS: StarterTemplate[] = [
  {
    id: 'professional-credit-note',
    name: 'Credit Note',
    description:
      'Professional credit note with credit summary box, linked invoice reference, reason block, and original invoice line items.',
    category: 'credit_note',
    thumbnail: 'credit-note',
    header_html: CREDIT_NOTE_HEADER,
    body_html: CREDIT_NOTE_BODY,
    footer_html: CREDIT_NOTE_FOOTER,
    css: '',
    sample_data: CREDIT_NOTE_SAMPLE_DATA,
  },
  {
    id: 'professional-debit-note',
    name: 'Debit Note',
    description: 'Professional debit note with amount-due box, reason, and totals summary.',
    category: 'debit_note',
    thumbnail: 'debit-note',
    header_html: DEBIT_NOTE_HEADER,
    body_html: DEBIT_NOTE_BODY,
    footer_html: DEBIT_NOTE_FOOTER,
    css: '',
    sample_data: DEBIT_NOTE_SAMPLE_DATA,
  },
  {
    id: 'professional-write-off',
    name: 'Write-Off Notice',
    description:
      'Write-off document with amount summary, linked invoice, reason block, and original invoice items.',
    category: 'write_off',
    thumbnail: 'write-off',
    header_html: WRITE_OFF_HEADER,
    body_html: WRITE_OFF_BODY,
    footer_html: WRITE_OFF_FOOTER,
    css: '',
    sample_data: WRITE_OFF_SAMPLE_DATA,
  },
  {
    id: 'professional-advance',
    name: 'Advance Receipt',
    description:
      'Advance receipt with total / available / used balance cards, line items table, tax breakdown, and notes.',
    category: 'advance',
    thumbnail: 'advance',
    header_html: ADVANCE_HEADER,
    body_html: ADVANCE_BODY,
    footer_html: ADVANCE_FOOTER,
    css: '',
    sample_data: ADVANCE_SAMPLE_DATA,
  },
  {
    id: 'professional-quote',
    name: 'Professional Quote',
    description:
      'Full-detail quote with hours, rate, discount, and tax columns. Shows gross total, discount, and net total breakdown.',
    category: 'quote',
    thumbnail: 'quote',
    header_html: QUOTE_HEADER,
    body_html: QUOTE_BODY,
    footer_html: QUOTE_FOOTER,
    css: QUOTE_CSS,
    sample_data: QUOTE_SAMPLE_DATA,
  },
  {
    id: 'epc-invoice',
    name: 'EPC Invoice',
    description:
      'GTA/EPC-style invoice with client code, description-only line items, HST breakdown, and payment options.',
    category: 'invoice',
    thumbnail: 'invoice',
    header_html: EPC_HEADER,
    body_html: EPC_BODY,
    footer_html: EPC_FOOTER,
    css: EPC_CSS,
    sample_data: EPC_SAMPLE_DATA,
  },
  {
    id: 'professional-invoice',
    name: 'Professional Invoice',
    description: 'Clean, modern invoice with line items table, tax breakdown, and payment details.',
    category: 'invoice',
    thumbnail: 'invoice',
    header_html: INVOICE_HEADER,
    body_html: INVOICE_BODY,
    footer_html: INVOICE_FOOTER,
    css: '',
    sample_data: INVOICE_SAMPLE_DATA,
  },
  {
    id: 'minimal-invoice',
    name: 'Minimal Invoice',
    description: 'Simple and clean invoice layout with essential information only.',
    category: 'invoice',
    thumbnail: 'invoice-minimal',
    header_html: `<div style="margin-bottom:20px;"><h1 style="font-size:24px;margin:0;">{{firm.name}}</h1><p style="color:#6b7280;font-size:11px;margin:4px 0 0;">Invoice {{invoice.number}} | {{formatDate invoice.issue_date}}</p></div>`,
    body_html: INVOICE_BODY,
    footer_html: `<p style="text-align:center;font-size:10px;color:#9ca3af;margin-top:20px;">Due by {{formatDate invoice.due_date}} | {{firm.email}}</p>`,
    css: '',
    sample_data: INVOICE_SAMPLE_DATA,
  },
  {
    id: 'data-report',
    name: 'Data Report',
    description: 'Tabular report layout with filter summary, column headers, and data rows.',
    category: 'report',
    thumbnail: 'report',
    header_html: REPORT_HEADER,
    body_html: REPORT_BODY,
    footer_html: REPORT_FOOTER,
    css: '',
    sample_data: REPORT_SAMPLE_DATA,
  },
  {
    id: 'client-statement',
    name: 'Client Statement',
    description: 'Ledger-style statement showing transactions with running balance.',
    category: 'statement',
    thumbnail: 'statement',
    header_html: INVOICE_HEADER.replace('INVOICE', 'STATEMENT')
      .replace('Invoice #:', 'Statement Period:')
      .replace(
        '{{invoice.number}}',
        '{{formatDate statement.start_date}} - {{formatDate statement.end_date}}',
      ),
    body_html: STATEMENT_BODY,
    footer_html: INVOICE_FOOTER,
    css: '',
    sample_data: STATEMENT_SAMPLE_DATA,
  },
  {
    id: 'blank',
    name: 'Blank Template',
    description: 'Start from scratch with an empty template. Best for custom documents.',
    category: 'invoice',
    thumbnail: 'blank',
    header_html: '',
    body_html: `<div style="padding:20px;">\n  <h1>{{firm.name}}</h1>\n  <p>Document generated on {{formatDate generated_date}}</p>\n  <!-- Start building your template here -->\n</div>`,
    footer_html: '',
    css: '',
    sample_data: {},
  },
];

const THUMBNAIL_ICONS: Record<string, JSX.Element> = {
  'credit-note': (
    <svg
      className="h-12 w-12 text-emerald-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ),
  'debit-note': (
    <svg
      className="h-12 w-12 text-amber-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
      />
    </svg>
  ),
  'write-off': (
    <svg
      className="h-12 w-12 text-violet-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ),
  advance: (
    <svg
      className="h-12 w-12 text-cyan-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
      />
    </svg>
  ),
  quote: (
    <svg
      className="h-12 w-12 text-sky-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
      />
    </svg>
  ),
  invoice: (
    <svg
      className="h-12 w-12 text-indigo-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
      />
    </svg>
  ),
  'invoice-minimal': (
    <svg
      className="h-12 w-12 text-emerald-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
      />
    </svg>
  ),
  report: (
    <svg
      className="h-12 w-12 text-amber-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6"
      />
    </svg>
  ),
  statement: (
    <svg
      className="h-12 w-12 text-blue-500"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
      />
    </svg>
  ),
  blank: (
    <svg
      className="h-12 w-12 text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  ),
};

/**
 * Starter template picker shown when creating a new template.
 * Lets users choose from pre-built templates or start blank.
 */
export function StarterTemplates({ onSelect, onSkip }: StarterTemplatesProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Choose a Starting Point
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Pick a pre-built template to customize, or start from scratch.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {STARTERS.map((starter) => (
          <button
            key={starter.id}
            onClick={() => onSelect(starter)}
            className="group relative flex flex-col items-center rounded-xl border-2 border-gray-200 p-6 text-center transition-all hover:border-indigo-400 hover:shadow-md dark:border-gray-700 dark:hover:border-indigo-500"
          >
            <div className="mb-3 flex h-20 w-20 items-center justify-center rounded-lg bg-gray-50 group-hover:bg-indigo-50 dark:bg-gray-800 dark:group-hover:bg-indigo-900/20">
              {THUMBNAIL_ICONS[starter.thumbnail] || THUMBNAIL_ICONS.blank}
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{starter.name}</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
              {starter.description}
            </p>
            <span className="mt-2 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {starter.category}
            </span>
          </button>
        ))}
      </div>

      <div className="text-center">
        <button
          onClick={onSkip}
          className="text-sm text-gray-500 underline hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          Skip and start with a blank editor
        </button>
      </div>
    </div>
  );
}
